import * as React from "react";
import { ToolButton } from "ui/Scripts/source/components/framework/toolButton";
import { Base } from "ui/Scripts/source/framework/base";
import { IIdTitle, IdTitle } from "ui/Scripts/source/models/common/idTitle";
import { ApplicationPermissionType } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import { IListFilterItem, ListFilterItem } from "../../../models/common/listFilterItem";
import { ISavedListFilter } from "../../../models/common/savedListFilter";
import { ITypedOption } from "../../../models/common/typedOption";
import { ILogPage } from "../../../models/log/logPage";
import { ILogProperties } from "../../../models/log/logProperties";
import * as logService from "../../../models/services/logService";
import { recordLockManager } from "../../../models/services/recordLockService";
import { IAppSessionState } from "../../../models/store/storeTypes";
import { HeaderMenuItem, NavigationMenu } from "../../app/NavigationMenu";
import { UserMenu } from "../../app/UserMenu";
import { ListFilterSaveDialog } from "../../framework/Filters/ListFilterSaveDialog";
import { PageContainer } from "../../framework/Page/PageContainer";
import { PageContent } from "../../framework/Page/PageContent";
import { PageFilterConditionDrawer } from "../../framework/Page/PageFilterConditionDrawer";
import { PageTopBar } from "../../framework/Page/PageTopBar";
import { IPageNavMenuItem } from "../../framework/PageNav/PageNavMenuItems";
import { FilterTabs } from "../../framework/PageNavMenuItem/FilterTabs";
import { LogFilterDialog } from "../LogFilterDialog";
import { LogList } from "../LogList";
import { LogPropertiesDialog, LogSettingsDialogMenuItem } from "../LogPropertiesDialog";
import { abortManager } from "source/models/services/tokenService";

/* eslint-disable no-unused-vars */
const enum LogPageTimeFilterId {
    None = "0",
    Today = "1",
    Yesterday = "2",
    ThisWeek = "3",
    Last30Days = "4",
    Interval = "5"
}

const enum LogPageEventTypeFilterId {
    None = "0",
    Information = "4",
    Warning = "2",
    Error = "1"
}
/* eslint-enable no-unused-vars */

// LogPage
export interface ILogPageOwnProps {
}

export interface ILogPageStateProps {
    appState: IAppSessionState;
    dataId: number;
    logPage: ILogPage;
}

export interface ILogPageDispatchProps {
    onGetLogPage: () => void;
    onLogout: () => void;
    onSetCulture: (culture: string) => void;
    onSaveUserParameters: (parameters: ITypedOption[]) => void;
    onRefreshSessionState: () => void;
}

type LogPageProp = ILogPageOwnProps & ILogPageStateProps & ILogPageDispatchProps;

interface ILogPageState {
    initialized: boolean;
    activeCompanyId: string;
    selectedLogEventTypes: string[];
    selectedTimeFilterId: LogPageTimeFilterId;
    timeFilters: IIdTitle[];
    eventTypes: IIdTitle[];
    startDateStr: string;
    endDateStr: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    settingsActions: IPageNavMenuItem[];
    // Filters
    logFilterIsSet: boolean;
    showLogFilterDialog: boolean;
    logFilterDialogFilter: IListFilterItem;
    filters: IListFilterItem[];
    savedFilters: IListFilterItem[];
    activeFilterId: string;
    activeSavedFilterId: string;
    showListFilterSaveDialog: boolean;
    listFilterSaveDialogFilter: IListFilterItem;
    listFilterSaveDialogGeneralFilter: any;
    listFilterSaveDialogReservedNames: string[];
    listFilterSaveDialogOnGetGeneralFilter: () => any;
    // Properties
    dataId: number;
    showLogPropertiesDialog: boolean;
    logProperties: ILogProperties;
}

export class LogPage extends React.Component<LogPageProp, ILogPageState> {
    getTimeFilters = (): IIdTitle[] => {
        const result: IIdTitle[] = [];
        result.push(IdTitle.createIdTitle(LogPageTimeFilterId.None, Translations.WholeHistory));
        result.push(IdTitle.createIdTitle(LogPageTimeFilterId.Today, Translations.Today));
        result.push(IdTitle.createIdTitle(LogPageTimeFilterId.Yesterday, Translations.Yesterday));
        result.push(IdTitle.createIdTitle(LogPageTimeFilterId.ThisWeek, Translations.Last7Days));
        result.push(IdTitle.createIdTitle(LogPageTimeFilterId.Last30Days, Translations.Last30Days));
        result.push(IdTitle.createIdTitle(LogPageTimeFilterId.Interval, Translations.Interval));
        return result;
    };
    
    getEventTypes = (): IIdTitle[] => {
        const result: IIdTitle[] = [];
        result.push(IdTitle.createIdTitle(LogPageEventTypeFilterId.None, "'" + Translations.All + "'"));
        result.push(IdTitle.createIdTitle(LogPageEventTypeFilterId.Information, Translations.Info));
        result.push(IdTitle.createIdTitle(LogPageEventTypeFilterId.Warning, Translations.Warning));
        result.push(IdTitle.createIdTitle(LogPageEventTypeFilterId.Error, Translations.Error));
        return result;
    };
    
    getFilters = (): IListFilterItem[] => {
        const result: IListFilterItem[] = [];
        const nowDate = Base.getNowUtcDate();
        result.push(ListFilterItem.createListFilterItem(Translations.Log, Translations.Log,
            {
                logEventTypes: [LogPageEventTypeFilterId.None],
                timeFilterId: LogPageTimeFilterId.Last30Days.toInteger(),
                startDateStr: Base.utcTimeToDateStr(nowDate.addDays(-60).getTime()),
                endDateStr: Base.utcTimeToDateStr(nowDate.getTime())
            }, "modifyDate", false));
        ListFilterItem.sortListFilterItems(result);
        return result;
    };

    getSavedFilters = (props: LogPageProp): IListFilterItem[] => {
        const result: IListFilterItem[] = [];
        if (!props || !props.logPage) return result;
        for (const companyFilter of props.logPage.companyFilters) {
            result.push(ListFilterItem.createListFilterItemFromSavedListFilter(companyFilter, true));
        }
        for (const userFilter of props.logPage.userFilters) {
            result.push(ListFilterItem.createListFilterItemFromSavedListFilter(userFilter, false));
        }
        ListFilterItem.sortListFilterItems(result);
        return result;
    };

    getLogFilterIsSet = (state: ILogPageState): boolean => {
        return LogFilterDialog.filterIsSet(state.timeFilters, state.eventTypes,
            state.selectedTimeFilterId, state.selectedLogEventTypes);
    };

    getSettingsActions = (activeCompanyId: string): IPageNavMenuItem[] => {
        const props = this.props;
        const sessionState = props.appState.state;
        const result: IPageNavMenuItem[] = [];
        if (activeCompanyId === Base.emptyGuid) {
            result.push({ id: LogSettingsDialogMenuItem.LogProperties, title: Translations.LogModuleProperties });
        } else if (sessionState.isCompanyAdmin) {
            result.push({ id: LogSettingsDialogMenuItem.Permissions, title: Translations.Permissions });
        }
        return result;
    };
    
    constructor(props: LogPageProp) {
        super(props);
        abortManager.abortAll(); //Abort all abortable calls regardless of module
        const filters = this.getFilters();
        const filterProps = ListFilterItem.getFilterProps(filters[0], "", true, this.getStateFilterPropsFromGeneralFilter);
        const state = {
            ...filterProps,
            initialized: !!props.logPage,
            activeCompanyId: props.appState.state.isSuperAdmin
                ? Base.emptyGuid
                : props.appState.state.companyId,
            timeFilters: this.getTimeFilters(),
            eventTypes: this.getEventTypes(),
            settingsActions: props.logPage && props.logPage.companies.length > 0
                ? this.getSettingsActions(props.logPage.companies[0].id)
                : [],
            // Filters
            logFilterIsSet: false,
            activeFilterId: filters[0].id,
            activeSavedFilterId: "",
            filters,
            savedFilters: this.getSavedFilters(props),
            showLogFilterDialog: false,
            logFilterDialogFilter: null,
            showListFilterSaveDialog: false,
            listFilterSaveDialogFilter: null,
            listFilterSaveDialogGeneralFilter: null,
            listFilterSaveDialogReservedNames: [],
            listFilterSaveDialogOnGetGeneralFilter: null,
            // Properties
            dataId: props.dataId,
            showLogPropertiesDialog: false,
            logProperties: null
        };
        this.state = {
            ...state,
            logFilterIsSet: this.getLogFilterIsSet(state)
        };
    }

    componentDidMount(): void {
        const sessionState = this.props.appState.state;
        recordLockManager.unlockAll(sessionState?.companyId, sessionState?.code);
        this.props.onGetLogPage();
    }

    componentDidUpdate(prevProps: LogPageProp, prevState: ILogPageState): void {
        const props = this.props;
        const state = this.state;
        const prevCompanyIds = (prevProps.logPage ? prevProps.logPage.companies.map(i => i.id) : []).join(",");
        const companyIds = (props.logPage ? props.logPage.companies.map(i => i.id) : []).join(",");
        const prevCompanyId = prevProps.logPage && prevProps.logPage.companies.length > 0 ? prevProps.logPage.companies[0].id : Base.emptyGuid;
        const companyId = props.logPage && props.logPage.companies.length > 0 ? props.logPage.companies[0].id : Base.emptyGuid;
        if (prevProps.dataId === props.dataId && prevCompanyId === companyId && prevCompanyIds === companyIds) return;
        const savedFilters = this.getSavedFilters(props);
        const activeSavedFilterId = savedFilters.find(i => i.id === state.activeSavedFilterId)
            ? state.activeSavedFilterId
            : "";
        const stateChange = {
            dataId: props.dataId,
            initialized: !!props.logPage,
            activeCompanyId: companyId,
            savedFilters,
            activeSavedFilterId
        };
        const newState = { ...state, ...stateChange };
        this.setState({
            ...stateChange,
            logFilterIsSet: this.getLogFilterIsSet(newState),
            settingsActions: this.getSettingsActions(newState.activeCompanyId)
        });
    }

    handleChangeActiveCompanyId = (companyId: string) => {
        if (this.state.activeCompanyId === companyId) return;
        this.setState({ activeCompanyId: companyId });
    };

    // #region Saved Filter
    getGeneralFilterFromState = (state: ILogPageState): any => {
        return {
            logEventTypes: state.selectedLogEventTypes ? state.selectedLogEventTypes.filter(i => i !== LogPageEventTypeFilterId.None) : [],
            timeFilterId: state.selectedTimeFilterId ? parseInt(state.selectedTimeFilterId, 10) : 0,
            startDateStr: state.startDateStr ?? "",
            endDateStr: state.endDateStr ?? ""
        };
    };
    
    getStateFilterPropsFromGeneralFilter = (generalFilter: any): any => {
        return {
            selectedLogEventTypes: generalFilter && generalFilter.logEventTypes && generalFilter.logEventTypes.length > 0 ? generalFilter.logEventTypes : [LogPageEventTypeFilterId.None],
            selectedTimeFilterId: generalFilter && !Base.isNullOrUndefined(generalFilter.timeFilterId) ? generalFilter.timeFilterId.toString(10) : LogPageTimeFilterId.None,
            startDateStr: generalFilter && generalFilter.startDateStr ? generalFilter.startDateStr : "",
            endDateStr: generalFilter && generalFilter.endDateStr ? generalFilter.endDateStr : "",
        };
    };
    
    getFilterPropsWithState = (listItemFilter: IListFilterItem) => {
        const state = this.state;
        let result = {
            selectedLogEventTypes: state.selectedLogEventTypes,
            selectedTimeFilterId: state.selectedTimeFilterId,
            startDateStr: state.startDateStr,
            endDateStr: state.endDateStr,
            sortColumn: state.sortColumn,
            sortOrderIsAsc: state.sortOrderIsAsc
        };
        if (listItemFilter) {
            result = ListFilterItem.getFilterProps(listItemFilter, result.sortColumn, result.sortOrderIsAsc, this.getStateFilterPropsFromGeneralFilter);
        }
        return result;
    };

    handleChangeActiveFilter = (activeFilterId: string) => {
        const state = this.state;
        const filter = state.filters.find(i => i.id === activeFilterId);
        if (!filter) return;
        const filterProps = this.getFilterPropsWithState(filter);
        const activeSavedFilterId = "";
        const newState = { ...state, ...filterProps, activeFilterId, activeSavedFilterId };
        this.setState({
            ...filterProps,
            activeFilterId,
            activeSavedFilterId: "",
            logFilterIsSet: this.getLogFilterIsSet(newState)
        });
    };

    handleChangeActiveSavedFilter = (activeSavedFilterId: string) => {
        const state = this.state;
        const savedFilter = state.savedFilters.find(i => i.id === activeSavedFilterId);
        if (!savedFilter) return;
        const filterProps = this.getFilterPropsWithState(savedFilter);
        const activeFilterId = "";
        const newState = { ...state, ...filterProps, activeFilterId, activeSavedFilterId };
        this.setState({
            ...filterProps,
            activeFilterId,
            activeSavedFilterId,
            logFilterIsSet: this.getLogFilterIsSet(newState)
        });
    };

    handleRemoveSavedFilter = async (savedFilterId: string) => {
        const state = this.state;
        const savedFilter = state.savedFilters.find(i => i.id === savedFilterId);
        if (!savedFilter) return;
        const result = await ListFilterSaveDialog.removeListFilterFromDb(state.savedFilters, state.activeCompanyId, savedFilterId, savedFilter.isShared, ApplicationPermissionType.Log);
        if (!result) return;
        const savedFilters = state.savedFilters.filter(i => i.id !== savedFilterId);
        let activeFilterId = "";
        let activeSavedFilterId = "";
        let newfilter: IListFilterItem = null;
        if (savedFilters.length > 0) {
            newfilter = savedFilters[0];
            activeSavedFilterId = newfilter.id;
        } else if (state.filters.length > 0) {
            newfilter = state.filters[0];
            activeFilterId = newfilter.id;
        }
        const filterProps = this.getFilterPropsWithState(newfilter);
        const newState = { ...state, ...filterProps, activeFilterId, activeSavedFilterId, savedFilters };
        this.setState({
            ...filterProps,
            showLogFilterDialog: false,
            activeFilterId,
            activeSavedFilterId,
            savedFilters,
            logFilterIsSet: this.getLogFilterIsSet(newState)
        });
    };

    handleFilterSortData = (sortColumn: string, sortOrderIsAsc: boolean) => {
        this.setState({
            sortColumn,
            sortOrderIsAsc,
        });
    };

    updateFiltersAfterSave = (orgFilterId: string, savedListFilter: ISavedListFilter, isShared: boolean) => {
        if (!savedListFilter) return;
        const state = this.state;
        const savedFilters = state.savedFilters.filter(i => i.id !== orgFilterId || i.isShared !== isShared);
        const filter = ListFilterItem.createListFilterItemFromSavedListFilter(savedListFilter, isShared);
        savedFilters.push(filter);
        ListFilterItem.sortListFilterItems(savedFilters);
        this.setState({
            showListFilterSaveDialog: false,
            savedFilters,
            activeFilterId: "",
            activeSavedFilterId: savedListFilter.id,
            logFilterDialogFilter: filter
        });
    };

    getActiveFilter = (): IListFilterItem => {
        const state = this.state;
        return state.activeFilterId
            ? state.filters.find(i => i.id === state.activeFilterId)
            : state.savedFilters.find(i => i.id === state.activeSavedFilterId);
    };

    handleSaveListFilter = async (saveAs: boolean, onGetGeneralFilter: () => any) => {
        const state = this.state;
        const filter = this.getActiveFilter();
        if (!filter) return;
        if (!saveAs) {
            const savedListFilter = await ListFilterSaveDialog.saveListFilterToDb(filter, this.getGeneralFilterFromState(state), state.sortColumn, state.sortOrderIsAsc, state.activeCompanyId, ApplicationPermissionType.Log);
            this.updateFiltersAfterSave(filter.id, savedListFilter, filter.isShared);
        } else {
            this.setState({
                showListFilterSaveDialog: true,
                listFilterSaveDialogFilter: filter,
                listFilterSaveDialogReservedNames: state.savedFilters.map(i => i.id).concat(state.filters.map(i => i.title)),
                listFilterSaveDialogGeneralFilter: this.getGeneralFilterFromState(state),
                listFilterSaveDialogOnGetGeneralFilter: onGetGeneralFilter
            });
        }
    };

    handleSaveListFilterDialogOk = (savedListFilter: ISavedListFilter, isShared: boolean) => {
        this.updateFiltersAfterSave("", savedListFilter, isShared);
    };

    handleSaveListFilterDialogCancel = () => {
        this.setState({
            showListFilterSaveDialog: false
        });
    };
    // #endregion Saved Filter

    // #region Log Filter
    handleOpenLogFilterDialog = () => {
        const filter = this.getActiveFilter();
        if (!filter) return;
        this.setState({
            showLogFilterDialog: true,
            logFilterDialogFilter: filter
        });
    };

    handleLogFilterDialogOk = (closeDialog: boolean, selectedTimeFilterId: LogPageTimeFilterId, selectedLogEventTypes: string[], startDateStr: string, endDateStr: string): any => {
        const newState = { ...this.state, selectedTimeFilterId, selectedLogEventTypes, startDateStr, endDateStr };
        this.setState({
            showLogFilterDialog: !closeDialog,
            selectedLogEventTypes,
            selectedTimeFilterId,
            startDateStr,
            endDateStr,
            logFilterIsSet: this.getLogFilterIsSet(newState)
        });
        return this.getGeneralFilterFromState(newState);
    };
    
    handleLogFilterDialogCancel = () => {
        this.setState({ showLogFilterDialog: false });
    };
    // #endregion Log Filter

    handleOpenLogProperties = async () => {
        const result = await AppUtils.callService(() => logService.getLogProperties(this.state.activeCompanyId), false);
        if (!result) return;
        this.setState({
            showLogPropertiesDialog: true,
            logProperties: result
        });
    };

    handleLogPropertiesDialogOk = () => {
        this.setState({
            dataId: Base.getRandomIntInteger(1000000),
            showLogPropertiesDialog: false
        });
    };

    handleLogPropertiesDialogCancel = () => {
        this.setState({
            showLogPropertiesDialog: false
        });
    };

    render() {
        const props = this.props;
        const state = this.state;
        const sessionState = props.appState.state;
        return (
            <PageContainer>
                <NavigationMenu
                    appState={props.appState}
                    activeMenuItem={HeaderMenuItem.Log}
                    companies={props.logPage ? props.logPage.companies : []}
                    activeCompanyId={state.activeCompanyId}
                    onChangeActiveCompany={this.handleChangeActiveCompanyId}
                />
                <PageContent>
                    <PageTopBar>
                        <UserMenu
                            appState={props.appState}
                            onLogout={props.onLogout}
                            onSetCulture={props.onSetCulture}
                            onRefreshSessionState={props.onRefreshSessionState}
                        />
                    </PageTopBar>
                    <LogList
                        savedFilter={state.activeSavedFilterId ? state.savedFilters.find(i => i.id === state.activeSavedFilterId) : null}
                        dataId={state.dataId.toString(10)}
                        preloaded={true}
                        waitingPreloadedData={true}
                        list={props.logPage ? props.logPage.list : null}
                        listFilterIsSet={state.logFilterIsSet}
                        types={state.selectedLogEventTypes}
                        companyId={state.activeCompanyId}
                        timeSpan={state.selectedTimeFilterId}
                        startDateStr={state.startDateStr}
                        endDateStr={state.endDateStr}
                        sortColumn={state.sortColumn}
                        sortOrderIsAsc={state.sortOrderIsAsc}
                        settingsActions={state.settingsActions}
                        titleSuffix={
                            <ToolButton
                                tooltip={state.logFilterIsSet ? Translations.FilterIsActive : Translations.FilterCondition}
                                classes={"titleSuffix " + (state.logFilterIsSet ? "filterSet" : "filterNotSet")}
                                onClick={this.handleOpenLogFilterDialog}
                            />
                        }
                        reportTabs={
                            <FilterTabs
                                items={state.savedFilters}
                                standardFilters={state.filters}
                                selectedId={this.state.activeFilterId}
                                selectedSavedId={this.state.activeSavedFilterId}
                                onSetActiveItem={this.handleChangeActiveSavedFilter}
                                onSetActiveStandardItem={this.handleChangeActiveFilter}
                            />
                        }
                        onOpenFiltersDialog={this.handleOpenLogFilterDialog}
                        onToggleSettingsDialog={this.handleOpenLogProperties}
                        onSetSortData={this.handleFilterSortData}
                    />
                </PageContent>
                <PageFilterConditionDrawer
                    open={state.showLogFilterDialog}
                    onClose={this.handleLogFilterDialogCancel}
                >
                    <LogFilterDialog
                        filter={state.logFilterDialogFilter}
                        eventTypes={state.eventTypes}
                        timeFilters={state.timeFilters}
                        selectedLogEventTypes={state.selectedLogEventTypes}
                        selectedTimeFilterId={state.selectedTimeFilterId}
                        startDateStr={state.startDateStr}
                        endDateStr={state.endDateStr}
                        canRemoveSharedFilters={sessionState.isCompanyAdmin}
                        onSaveFilter={this.handleSaveListFilter}
                        onRemoveFilter={this.handleRemoveSavedFilter}
                        onOk={this.handleLogFilterDialogOk}
                        onCancel={this.handleLogFilterDialogCancel}
                    />
                </PageFilterConditionDrawer>
                {state.showListFilterSaveDialog &&
                    <ListFilterSaveDialog
                        sessionState={sessionState}
                        companyId={state.activeCompanyId}
                        applicationPermissionType={ApplicationPermissionType.Log}
                        listFilter={state.listFilterSaveDialogFilter}
                        generalFilter={state.listFilterSaveDialogGeneralFilter}
                        sortColumn={state.sortColumn}
                        sortOrderIsAsc={state.sortOrderIsAsc}
                        reservedNames={state.listFilterSaveDialogReservedNames}
                        onGetGeneralFilter={state.listFilterSaveDialogOnGetGeneralFilter}
                        onOk={this.handleSaveListFilterDialogOk}
                        onCancel={this.handleSaveListFilterDialogCancel}
                    />
                }
                {state.showLogPropertiesDialog &&
                    <LogPropertiesDialog
                        companyId={state.activeCompanyId}
                        properties={state.logProperties}
                        settingsActions={state.settingsActions}
                        onOk={this.handleLogPropertiesDialogOk}
                        onCancel={this.handleLogPropertiesDialogCancel}
                    />
                }
            </PageContainer>
        );
    }
}